/* THE MAP'S VIEWPORT — pure, zero imports beyond the extent constants.
 *
 * Brian asked for zoom three times. The map had a STATIC viewBox and no zoom or
 * pan of any kind: `CrownMap.jsx` rendered `viewBox="0 0 960 600"` and the only
 * `transform` in the file was the crown marker's translate. So this is new
 * machinery, and it is a separate module for the reason every other model in
 * this folder is: the arithmetic that decides what is on screen is the part that
 * can be WRONG, and a component cannot be driven under Node.
 *
 * ⚠️ THE VIEW IS `{x, y, w}` AND THERE IS NO `h`. The height is DERIVED from the
 * extent's aspect ratio, which makes a distorted map unrepresentable rather than
 * merely discouraged. A `{x,y,w,h}` view invites an independent h — and a pinch
 * that scales the axes unevenly stretches the United States, which on a
 * choropleth reads as a data error (Texas looking wrong is indistinguishable
 * from Texas being wrong) rather than as a gesture bug.
 */

/* The extent, repeated rather than imported, so this file stays loadable under
 * Node with no module graph behind it — the same discipline canvaEntry.js and
 * canvaGap.js follow, and the reason their rules are assertable at all. */
export const EXTENT_W = 960
export const EXTENT_H = 600
export const ASPECT = EXTENT_H / EXTENT_W

/** Fully zoomed out: the whole of North America, exactly as it shipped. */
export const FULL_VIEW = { x: 0, y: 0, w: EXTENT_W }

/* ⚠️ MAX_K IS 12 BECAUSE OF RHODE ISLAND, NOT BECAUSE IT IS A ROUND NUMBER.
 * At the full extent RI is roughly four pixels across on a 390px phone — which
 * is the whole complaint. 12× puts it near a 48px tap target, i.e. above the
 * 44px floor this repo measures everywhere else. A cap below that would leave
 * the smallest states unreachable, which is the defect, not the fix. */
export const MIN_K = 1
export const MAX_K = 12

const clamp = (n, lo, hi) => (n < lo ? lo : n > hi ? hi : n)

/** Height of a view, always derived. */
export const heightOf = (view) => view.w * ASPECT

/** How far in we are. 1 = the whole extent. */
export const zoomOf = (view) => EXTENT_W / view.w

/**
 * The only function allowed to produce a view.
 *
 * ⚠️ IT CLAMPS THE WIDTH FIRST AND THE ORIGIN SECOND, AND THE ORDER IS LOAD
 * BEARING. Clamping the origin against an un-clamped width lets a view that is
 * wider than the extent push x negative and then "correct" it to 0, leaving the
 * map pinned to one corner with grey on two sides. Width first means the origin
 * is always clamped against a width the extent can actually contain.
 */
export function clampView(view) {
  const w = clamp(Number(view?.w) || EXTENT_W, EXTENT_W / MAX_K, EXTENT_W)
  const h = w * ASPECT
  return {
    x: clamp(Number(view?.x) || 0, 0, EXTENT_W - w),
    y: clamp(Number(view?.y) || 0, 0, EXTENT_H - h),
    w,
  }
}

/**
 * Zoom to `k`, holding the point under the fingers still.
 *
 * `fx` / `fy` are FRACTIONS of the current viewport (0..1), not pixels and not
 * user units — the caller owns the element geometry and this file owns the
 * arithmetic. Passing pixels here would make the model depend on a DOM rect,
 * which is exactly what makes a thing untestable.
 *
 * ⚠️ HOLDING THE FOCAL POINT IS WHAT MAKES A PINCH FEEL RIGHT. Zooming about the
 * centre instead is a one-line simplification that sends the thing under your
 * fingers sliding off screen, and it reads as the map fighting you.
 */
export function zoomTo(view, k, fx = 0.5, fy = 0.5) {
  const cur = clampView(view)
  const nextK = clamp(Number(k) || MIN_K, MIN_K, MAX_K)
  const w = EXTENT_W / nextK
  const h = w * ASPECT
  /* The user-space point under the focal fraction, before and after. */
  const ax = cur.x + cur.w * clamp(fx, 0, 1)
  const ay = cur.y + heightOf(cur) * clamp(fy, 0, 1)
  return clampView({ x: ax - w * clamp(fx, 0, 1), y: ay - h * clamp(fy, 0, 1), w })
}

/** Multiply the current zoom — what a wheel notch and a +/− button both do. */
export const zoomBy = (view, factor, fx, fy) =>
  zoomTo(view, zoomOf(view) * (Number(factor) || 1), fx, fy)

/**
 * Pan by a fraction of the CURRENT viewport, so a drag moves the map by the
 * same amount of finger travel at every zoom level. Expressed in viewport
 * fractions for the same reason as the focal point: no DOM in this file.
 */
export function panByFraction(view, dxFrac, dyFrac) {
  const cur = clampView(view)
  return clampView({
    x: cur.x + cur.w * (Number(dxFrac) || 0),
    y: cur.y + heightOf(cur) * (Number(dyFrac) || 0),
    w: cur.w,
  })
}

/**
 * Frame a region's bounding box — what "Zoom to Rhode Island" does.
 *
 * ⚠️ `pad` IS A MULTIPLIER, NOT A MARGIN IN USER UNITS. A fixed margin that
 * looks generous around Texas swallows Rhode Island whole. 1.6 leaves the state
 * filling a bit under two-thirds of the frame, so its neighbours stay visible —
 * a state framed edge-to-edge with no surroundings is disorienting, because the
 * thing that tells you where you are is what is next to it.
 */
export function fitToBox(box, pad = 1.6) {
  if (!box || !(box.width > 0) || !(box.height > 0)) return FULL_VIEW
  const p = Math.max(1, Number(pad) || 1)
  /* Fit the LARGER of the two demands, so neither axis crops the state. */
  const w = Math.max(box.width * p, (box.height * p) / ASPECT)
  const cx = box.x + box.width / 2
  const cy = box.y + box.height / 2
  return clampView({ x: cx - w / 2, y: cy - (w * ASPECT) / 2, w })
}

/** `viewBox` attribute for a view. */
export const viewBoxOf = (view) => {
  const v = clampView(view)
  return `${v.x} ${v.y} ${v.w} ${heightOf(v)}`
}

export const isFullyOut = (view) => zoomOf(view) <= MIN_K + 1e-9

/**
 * ⚠️ THE SCROLL-TRAP RULE, AND IT IS THE DIFFERENCE BETWEEN A MAP AND A WALL.
 *
 * `touch-action: none` on a map the height of a phone screen means a single
 * finger anywhere on it can no longer scroll the PAGE — the reader is stuck and
 * has to find a sliver of margin to get past. So the map only takes the gesture
 * once it has something to do with it:
 *
 *   fully zoomed out → `pan-y`: one finger scrolls the page as it always did,
 *                      and a two-finger pinch still reaches us (pan-y disallows
 *                      the browser's own pinch, so the gesture is ours).
 *   zoomed in        → `none`: one finger pans the map, which is now the
 *                      obviously intended meaning of a drag.
 *
 * Reset therefore hands page scrolling back, which is why it is a real control
 * and not just a convenience.
 */
export const touchActionFor = (view) => (isFullyOut(view) ? 'pan-y' : 'none')
